<script setup lang="ts">
import SettingFormInput from "@/components/VehicleFormParts/SettingFormInput.vue";
import Toggle from "@/components/VehicleFormParts/SettingFormToggle.vue";
import {computed, reactive, defineProps, ref, toRefs, watch} from 'vue'
import i18n from '@/lang/i18n';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  selectDefault: Boolean,
  errors: { type: Object, default: () => ({}) },
  formInputs: {},
  operationOfficeId: null,
  deviceId: null,
  isInstallationOffice: {
    type: Boolean,
    default: false
  },
})
const isSpeedLimitPreAlert = ref('OFF');
const setPreDelayTime = ref<number>('0');
const isAlertPreTiming = ref('0');
const setSpeedLimitPreInterval = ref<string>('15');
const selectItemSpeedLimitInterval = reactive([
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.continuous'), value: '0' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.fifteenSeconds'), value: '15' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.thirtySeconds'), value: '30' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.fortyFiveSeconds'), value: '45' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.sixtySeconds'), value: '60' },
]);
const selectItemSpeedLimitTiming = reactive([
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.onlyExcess'), value: '0' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.continuation'), value: '1' },
]);
const readonlyInputs = ref<any[]>([]);
const { formInputs } = toRefs(props);
const errorKeyPrefix = ref('initial_params.speed_alert');

const form = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
})

const formFieldOptions = computed(() => {
  const parentKey = 'speed_alert';

  return {
    isSpeedLimitPreAlert: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_pre_on', 'speed_limit_normal_pre_on']),
    isAlertPreTiming: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_pre_timing', 'speed_limit_normal_pre_timing']),
    speedLimitHighPreThreshold: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_pre_threshold']),
    speedLimitNormalPreThreshold: getShowAndRequiredAndErrors(parentKey, ['speed_limit_normal_pre_threshold']),
    setPreDelayTime: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_pre_delay', 'speed_limit_normal_pre_delay']),
  };
});

const isToggleDisabled = computed((): boolean => {
  return (
      (props.selectDefault && readonlyInputs.value.includes('speed_limit_normal_pre_on')
          && readonlyInputs.value.includes('speed_limit_high_pre_on'))
      || form.value.speed_limit_high_on === 'OFF'
      || form.value.speed_limit_normal_on === 'OFF'
  );
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, string | boolean>): void;
}>();

const autoCompleteToggles = () => {
  isSpeedLimitPreAlert.value = form.value.speed_limit_high_pre_on == 'ON' && form.value.speed_limit_normal_pre_on == 'ON' ? 'ON' : 'OFF';
  if (form.value.speed_limit_high_on == 'OFF' || form.value.speed_limit_normal_on == 'OFF') {
    isSpeedLimitPreAlert.value = 'OFF'
  }
  isAlertPreTiming.value = form.value.speed_limit_high_pre_timing == '1' && form.value.speed_limit_normal_pre_timing == '1' ? '1' : '0';
};

const autoCompleteDelays = () => {
  if (form.value.speed_limit_normal_pre_delay && form.value.speed_limit_high_pre_delay) {
    setPreDelayTime.value = form.value.speed_limit_normal_pre_delay;
  }
};

const autoCompleteSelects = () => {
  if (form.value.speed_limit_normal_pre_interval && form.value.speed_limit_high_pre_interval) {
    setSpeedLimitPreInterval.value = form.value.speed_limit_normal_pre_interval;
  }
};

const getShowAndRequiredAndErrors = (parentKey: string, formKeys: string[]) => {
  if (!props.formInputs?.form[parentKey]) {
    return {
      isShow: false,
      isRequired: false,
      errors: null
    };
  }

  const keys = Object.keys(props.formInputs.form[parentKey]);

  const result = formKeys.map(formKey => {
    return {
      isShow: keys.includes(formKey),
      isRequired: !!props.formInputs.form[parentKey][formKey]?.required,
      errors: props.errors[`${errorKeyPrefix.value}.${formKey}`]
    };
  });

  return {
    isShow: result.every(item => item.isShow),
    isRequired: result.every(item => item.isRequired),
    errors: result.map(item => item.errors).filter(error => error)
  };
};

watch(
    () => form.value.speed_limit_high,
    (newValue: number) => {
      if (newValue < form.value.speed_limit_high_pre_threshold) {
        form.value.speed_limit_high_pre_threshold = newValue < 5 ? 0 : 5; //init
      }
    }
);

watch(
    () => form.value.speed_limit_normal,
    (newValue: number) => {
      if (newValue < form.value.speed_limit_normal_pre_threshold) {
        form.value.speed_limit_normal_pre_threshold = newValue < 5 ? 0 : 5; //init
      }
    }
);

watch(
    () => form.value.speed_limit_high_on,
    (newValue: number) => {
      if (newValue == 'OFF') {
        isSpeedLimitPreAlert.value = 'OFF';
      }
    }
)

watch(() => isSpeedLimitPreAlert.value, (value) => {
  form.value.speed_limit_high_pre_on = value;
  form.value.speed_limit_normal_pre_on = value;
});

watch(() => setPreDelayTime.value, (value) => {
  form.value.speed_limit_high_pre_delay = value;
  form.value.speed_limit_normal_pre_delay = value;
});

watch(() => isAlertPreTiming.value, (value) => {
  form.value.speed_limit_high_pre_timing = value;
  form.value.speed_limit_normal_pre_timing = value;
});

watch(() => setSpeedLimitPreInterval.value, (value) => {
  form.value.speed_limit_high_pre_interval = value;
  form.value.speed_limit_normal_pre_interval = value;
});

watch(() => props.formInputs, () => {
  if (!props.formInputs?.form?.speed_alert) {
    return;
  }
  const key = Object.keys(props.formInputs.default_params)[0];
  readonlyInputs.value = Object.keys(props.formInputs.default_params[key]?.parameters?.speed_alert ?? []);
});

watch(() => props.modelValue, () => {
  if (props.modelValue) {
    autoCompleteToggles();
    autoCompleteDelays();
    autoCompleteSelects();
  }
});
</script>

<template>
  <div class="row mr-3 v-theme--light">
    <Toggle
      :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.speedLimitPreAlert')"
      v-model="isSpeedLimitPreAlert"
      :formInputs="formInputs"
      :isShow="formFieldOptions.isSpeedLimitPreAlert.isShow"
      :isRequired="formFieldOptions.isSpeedLimitPreAlert.isRequired"
      :disabled="isToggleDisabled || isInstallationOffice"
      :errors="formFieldOptions.isSpeedLimitPreAlert.errors"
    />
    <div class="row mr-3 v-theme--light ml-4 no-padding" v-if="isSpeedLimitPreAlert === 'ON'">
      <div class="row align-items-center no-padding">
        <div class="col-12 col-lg">
          <label class="form-label">
            {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.PreAlertSpeedSettings') }}
          </label>
        </div>
        <div class="col-12 col-lg text-right">
          <span>
            {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.expresswaySpeed', { speedLimit: form.speed_limit_high }) }}
          </span>
        </div>
        <div class="col">
          <SettingFormInput
              :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.expresswayKph')"
              v-model="form.speed_limit_high_pre_threshold"
              :isShow="formFieldOptions.speedLimitHighPreThreshold.isShow"
              :isRequired="formFieldOptions.speedLimitHighPreThreshold.isRequired"
              type="number"
              :max="form.speed_limit_high"
              :colClassNumber="0"
              :formInputs="formInputs"
              :readonly="(selectDefault && readonlyInputs.includes('speed_limit_high_pre_threshold')) || isInstallationOffice"
              :errors="formFieldOptions.speedLimitHighPreThreshold.errors"
          />
        </div>
        <div class="col-12 col-lg">
          <i18n-t keypath="pages.vehicles.form.iniFileSetting.speedAlert.preAlertInKph">
            <template #speed>
              <span class="pre-alert-speed">{{ form.speed_limit_high - form.speed_limit_high_pre_threshold }}</span>
            </template>
          </i18n-t>
        </div>
      </div>
      <div class="row align-items-center no-padding">
        <div class="col">
          <label class="form-label"></label>
        </div>
        <div class="col-12 col-lg text-right">
          <span>
            {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.generalRoadSpeed', { speedLimit: form.speed_limit_normal }) }}
          </span>
        </div>
        <div class="col">
          <SettingFormInput
              :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.generalRoadKph')"
              v-model="form.speed_limit_normal_pre_threshold"
              :isShow="formFieldOptions.speedLimitNormalPreThreshold.isShow"
              :isRequired="formFieldOptions.speedLimitNormalPreThreshold.isRequired"
              type="number"
              :max="form.speed_limit_normal"
              :colClassNumber="0"
              :formInputs="formInputs"
              :readonly="(selectDefault && readonlyInputs.includes('speed_limit_normal_pre_threshold')) || isInstallationOffice"
              :errors="formFieldOptions.speedLimitNormalPreThreshold.errors"
          />
        </div>
        <div class="col-12 col-lg">
          <i18n-t keypath="pages.vehicles.form.iniFileSetting.speedAlert.preAlertInKph">
            <template #speed>
              <span class="pre-alert-speed">{{ form.speed_limit_normal - form.speed_limit_normal_pre_threshold }}</span>
            </template>
          </i18n-t>
        </div>
      </div>
      <div class="row align-items-center no-padding">
        <div class="col-12 col-lg">
          <label class="form-label">
            {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.decisionTime') }}
          </label>
        </div>
        <div class="col">
          <SettingFormInput
              :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.decisionTimeSeconds')"
              v-model="setPreDelayTime"
              :isShow="formFieldOptions.setPreDelayTime.isShow"
              :isRequired="formFieldOptions.setPreDelayTime.isRequired"
              type="number"
              :max="600"
              :colClassNumber="0"
              :formInputs="formInputs"
              :readonly="(selectDefault && readonlyInputs.includes('speed_limit_normal_pre_delay') && readonlyInputs.includes('speed_limit_high_pre_delay')) || isInstallationOffice"
              :errors="formFieldOptions.setPreDelayTime.errors"
          />
        </div>
      </div>
      <div class="row align-items-center no-padding">
        <div class="col-12 col-lg">
          <label class="form-label">
            {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertTiming') }}
          </label>
        </div>
        <div class="col">
          <v-select
              hide-details="auto"
              class="small mt-2"
              :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertTimingLabel')"
              v-model="isAlertPreTiming"
              :items="selectItemSpeedLimitTiming"
              :clearable="false"
              :disabled="(selectDefault && readonlyInputs.includes('speed_limit_high_pre_timing') && readonlyInputs.includes('speed_limit_normal_pre_timing')) || isInstallationOffice"
          >
          </v-select>
        </div>
      </div>
      <div class="row align-items-center no-padding" v-if="isAlertPreTiming === '1'">
        <div class="col">
          <label class="form-label">
            {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertInterval') }}
          </label>
        </div>
        <div class="col">
          <v-select
              hide-details="auto"
              class="small mt-2"
              :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertInterval')"
              v-model="setSpeedLimitPreInterval"
              :items="selectItemSpeedLimitInterval"
              :clearable="false"
              :disabled="isInstallationOffice"
          >
          </v-select>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-padding {
  padding: 0 !important;
}
.headline {
  font-size: 24px;
  font-weight: bold;
  color: #0088a6;
}
.pre-alert-speed {
  font-size: 1.5em;
}
</style>
